(() => {
  'use strict';

  const normalise = (value) =>
    String(value || '').trim().toLowerCase();


  function findBySection(selector, sectionId) {
    return Array.from(
      document.querySelectorAll(selector)
    ).find(
      (element) =>
        element.dataset.sectionId === sectionId
    );
  }


  /* ========================================
     PERSONALISATION
     ======================================== */

  function initialiseControls(controls) {

    if (
      controls.dataset.tpInitialised === 'true'
    ) {
      return;
    }

    controls.dataset.tpInitialised = 'true';


    const sectionId =
      controls.dataset.sectionId;


    const preview = findBySection(
      '[data-tp-preview]',
      sectionId
    );


    if (!preview) return;


    /* Caption inputs */

    const lineOneInput =
      controls.querySelector(
        '[data-tp-caption-input="1"]'
      );


    const lineTwoInput =
      controls.querySelector(
        '[data-tp-caption-input="2"]'
      );


    /* Personalisation toggle */

    const personalisationToggle =
      controls.querySelector(
        '[data-tp-personalisation-toggle]'
      );


    const personalisationFields =
      controls.querySelector(
        '[data-tp-personalisation-fields]'
      );


    /* Preview elements */

    const lineOneBox =
      preview.querySelector(
        '[data-tp-preview-line="1"]'
      );


    const lineTwoBox =
      preview.querySelector(
        '[data-tp-preview-line="2"]'
      );


    const lineOneText =
      preview.querySelector(
        '[data-tp-preview-text="1"]'
      );


    const lineTwoText =
      preview.querySelector(
        '[data-tp-preview-text="2"]'
      );


    /* Form */

    const warning =
      controls.querySelector(
        '[data-tp-caption-warning]'
      );


    const form =
      controls.closest('form') ||
      document.getElementById(
        `product-form-${sectionId}`
      );


    const addButton =
      form?.querySelector(
        'button[name="add"]'
      );


    const variantInput =
      form?.querySelector(
        '.product-variant-id'
      );


    /* Default artwork caption */

    const defaultLineOne =
      preview.getAttribute(
        'data-default-line-1'
      ) || '';


    const defaultLineTwo =
      preview.getAttribute(
        'data-default-line-2'
      ) || '';


    let captionInvalid = false;


    /* ========================================
       CHARACTER COUNTER
       ======================================== */

    function updateCounter(
      input,
      lineNumber
    ) {

      const counter =
        controls.querySelector(
          `[data-tp-counter="${lineNumber}"]`
        );


      if (!counter || !input) return;


      const maximum =
        Number(
          input.getAttribute('maxlength')
        ) || 32;


      counter.textContent =
        `${input.value.length} / ${maximum}`;
    }


    /* ========================================
       SET CAPTION LINE
       ======================================== */

    function setLine(
      box,
      textElement,
      value
    ) {

      if (!box || !textElement) return;


      const cleanedValue =
        String(value || '').trim();


      textElement.textContent =
        cleanedValue;


      box.classList.toggle(
        'tp-is-hidden',
        cleanedValue.length === 0
      );
    }


    /* ========================================
       LIVE CAPTION PREVIEW
       ======================================== */

    function updateCaptionPreview() {

      const customLineOne =
        lineOneInput?.value.trim() || '';


      const customLineTwo =
        lineTwoInput?.value.trim() || '';


      const customerHasTyped =
        customLineOne.length > 0 ||
        customLineTwo.length > 0;


      if (customerHasTyped) {

        setLine(
          lineOneBox,
          lineOneText,
          customLineOne
        );


        setLine(
          lineTwoBox,
          lineTwoText,
          customLineTwo
        );

      } else {

        setLine(
          lineOneBox,
          lineOneText,
          defaultLineOne
        );


        setLine(
          lineTwoBox,
          lineTwoText,
          defaultLineTwo
        );
      }


      updateCounter(
        lineOneInput,
        1
      );


      updateCounter(
        lineTwoInput,
        2
      );


      requestAnimationFrame(() => {

        requestAnimationFrame(
          validateCaptionWidth
        );

      });
    }


    /* ========================================
       SHOW / HIDE PERSONALISATION
       ======================================== */

    function updatePersonalisationVisibility() {

      if (
        !personalisationToggle ||
        !personalisationFields
      ) {
        return;
      }


      const isPersonalising =
        personalisationToggle.checked;


      personalisationFields.hidden =
        !isPersonalising;


      if (lineOneInput) {

        lineOneInput.disabled =
          !isPersonalising;

      }


      if (lineTwoInput) {

        lineTwoInput.disabled =
          !isPersonalising;

      }


      if (!isPersonalising) {

        if (lineOneInput) {
          lineOneInput.value = '';
        }


        if (lineTwoInput) {
          lineTwoInput.value = '';
        }


        updateCaptionPreview();

      } else {

        validateCaptionWidth();

      }
    }


    /* ========================================
       CAPTION WIDTH VALIDATION
       ======================================== */

    function validateCaptionWidth() {

      const isPersonalising =
        personalisationToggle?.checked === true;


      const customLineOne =
        lineOneInput?.value.trim() || '';


      const customLineTwo =
        lineTwoInput?.value.trim() || '';


      const customerHasTyped =
        customLineOne.length > 0 ||
        customLineTwo.length > 0;


      if (
        !isPersonalising ||
        !customerHasTyped
      ) {

        captionInvalid = false;


        lineOneInput?.setAttribute(
          'aria-invalid',
          'false'
        );


        lineTwoInput?.setAttribute(
          'aria-invalid',
          'false'
        );


        if (warning) {

          warning.hidden = true;

        }


        updateAddButton();

        return;
      }


      const previewRectangle =
        preview.getBoundingClientRect();


      const safeWidthPercentage =
        Number(
          preview.dataset.safeWidth
        ) || 64;


      const horizontalInset =
        previewRectangle.width *
        ((100 - safeWidthPercentage) / 200);


      const verticalInset =
        previewRectangle.height * 0.035;


      const safeLeft =
        previewRectangle.left +
        horizontalInset;


      const safeRight =
        previewRectangle.right -
        horizontalInset;


      const safeTop =
        previewRectangle.top +
        verticalInset;


      const safeBottom =
        previewRectangle.bottom -
        verticalInset;


      const visibleLines =
        [lineOneBox, lineTwoBox].filter(
          (line) =>
            line &&
            !line.classList.contains(
              'tp-is-hidden'
            )
        );


      let lineOneInvalid = false;

      let lineTwoInvalid = false;


      visibleLines.forEach((line) => {

        const rectangle =
          line.getBoundingClientRect();


        const outsideSafeArea =
          rectangle.left < safeLeft ||
          rectangle.right > safeRight ||
          rectangle.top < safeTop ||
          rectangle.bottom > safeBottom;


        if (
          outsideSafeArea &&
          line === lineOneBox
        ) {

          lineOneInvalid = true;

        }


        if (
          outsideSafeArea &&
          line === lineTwoBox
        ) {

          lineTwoInvalid = true;

        }

      });


      captionInvalid =
        lineOneInvalid ||
        lineTwoInvalid;


      lineOneInput?.setAttribute(
        'aria-invalid',
        String(lineOneInvalid)
      );


      lineTwoInput?.setAttribute(
        'aria-invalid',
        String(lineTwoInvalid)
      );


      if (warning) {

        warning.hidden =
          !captionInvalid;

      }


      updateAddButton();
    }


    /* ========================================
       ADD TO CART
       ======================================== */

    function updateAddButton() {

      if (!addButton) return;


      const variantUnavailable =
        Boolean(
          variantInput?.disabled
        );


      addButton.disabled =
        captionInvalid ||
        variantUnavailable;


      addButton.classList.toggle(
        'tp-caption-invalid',
        captionInvalid
      );


      if (captionInvalid) {

        addButton.setAttribute(
          'aria-disabled',
          'true'
        );

      } else if (!variantUnavailable) {

        addButton.removeAttribute(
          'aria-disabled'
        );

      }
    }


    /* ========================================
       CAPTION BOX COLOUR
       ======================================== */

    function updateCaptionColour() {

      const selectedColour =
        controls.querySelector(
          '[data-tp-caption-colour]:checked'
        );


      if (!selectedColour) return;


      preview.style.setProperty(
        '--tp-caption-colour',
        selectedColour.dataset.colour
      );
    }


    /* ========================================
       SPLIT INSPIRATION CAPTION
       ======================================== */

    function splitCaptionIntoLines(caption) {

      const cleanedCaption =
        String(caption || '').trim();


      const words =
        cleanedCaption
          .split(/\s+/)
          .filter(Boolean);


      /*
        Empty caption
      */

      if (words.length === 0) {

        return {
          lineOne: '',
          lineTwo: ''
        };

      }


      /*
        SHORT CAPTIONS

        Keep captions of 26 characters or fewer
        together on one line.

        Example:
        "Did I leave the iron on?"

        becomes:

        Line 1: Did I leave the iron on?
        Line 2: blank
      */

      if (cleanedCaption.length <= 26) {

        return {
          lineOne: cleanedCaption,
          lineTwo: ''
        };

      }


      /*
        SINGLE WORD

        Keep a single word on line one.
      */

      if (words.length === 1) {

        return {
          lineOne: words[0],
          lineTwo: ''
        };

      }


      /*
        LONGER CAPTIONS

        Test every possible word break and choose
        the most visually balanced two-line split.
      */

      let bestLineOne = '';
      let bestLineTwo = '';
      let bestScore = Infinity;


      for (
        let splitIndex = 1;
        splitIndex < words.length;
        splitIndex++
      ) {

        const proposedLineOne =
          words
            .slice(0, splitIndex)
            .join(' ');


        const proposedLineTwo =
          words
            .slice(splitIndex)
            .join(' ');


        if (
          proposedLineOne.length > 32 ||
          proposedLineTwo.length > 32
        ) {
          continue;
        }


        const lengthDifference =
          Math.abs(
            proposedLineOne.length -
            proposedLineTwo.length
          );


        /*
          Slightly favour line 1 being the same
          length or a little longer than line 2.
        */

        const secondLineLongerPenalty =
          proposedLineTwo.length >
          proposedLineOne.length
            ? 2
            : 0;


        const score =
          lengthDifference +
          secondLineLongerPenalty;


        if (score < bestScore) {

          bestScore = score;

          bestLineOne =
            proposedLineOne;

          bestLineTwo =
            proposedLineTwo;

        }

      }


      if (
        bestLineOne ||
        bestLineTwo
      ) {

        return {
          lineOne: bestLineOne,
          lineTwo: bestLineTwo
        };

      }


      /*
        FALLBACK

        For unusually long captions,
        fill the two lines while keeping
        whole words wherever possible.
      */

      let fallbackLineOne = '';
      let fallbackLineTwo = '';


      words.forEach((word) => {

        const proposedLineOne =
          fallbackLineOne
            ? `${fallbackLineOne} ${word}`
            : word;


        if (
          fallbackLineTwo.length === 0 &&
          proposedLineOne.length <= 32
        ) {

          fallbackLineOne =
            proposedLineOne;

          return;

        }


        const proposedLineTwo =
          fallbackLineTwo
            ? `${fallbackLineTwo} ${word}`
            : word;


        if (
          proposedLineTwo.length <= 32
        ) {

          fallbackLineTwo =
            proposedLineTwo;

        }

      });


      return {
        lineOne: fallbackLineOne,
        lineTwo: fallbackLineTwo
      };
    }


    /* ========================================
       PERSONALISATION EVENTS
       ======================================== */

    lineOneInput?.addEventListener(
      'input',
      updateCaptionPreview
    );


    lineTwoInput?.addEventListener(
      'input',
      updateCaptionPreview
    );


    personalisationToggle?.addEventListener(
      'change',
      updatePersonalisationVisibility
    );


    controls
      .querySelectorAll(
        '[data-tp-caption-colour]'
      )
      .forEach((radio) => {

        radio.addEventListener(
          'change',
          updateCaptionColour
        );

      });


    /* Inspiration caption buttons */

    controls
      .querySelectorAll(
        '[data-tp-caption-inspiration]'
      )
      .forEach((button) => {

        button.addEventListener(
          'click',
          () => {

            const caption =
              button
                .getAttribute(
                  'data-tp-caption-inspiration'
                )
                ?.trim() || '';


            if (!caption) return;


            const splitCaption =
              splitCaptionIntoLines(
                caption
              );


            if (lineOneInput) {

              lineOneInput.value =
                splitCaption.lineOne;

            }


            if (lineTwoInput) {

              lineTwoInput.value =
                splitCaption.lineTwo;

            }


            updateCaptionPreview();

          }
        );

      });


    /* Prevent invalid caption from adding */

    form?.addEventListener(
      'submit',
      (event) => {

        validateCaptionWidth();


        if (!captionInvalid) return;


        event.preventDefault();

        event.stopImmediatePropagation();


        if (warning) {
          warning.hidden = false;
        }


        const firstInvalidInput =
          controls.querySelector(
            '[aria-invalid="true"]'
          );


        firstInvalidInput?.focus();

      }
    );


    /* Recheck if preview changes size */

    if ('ResizeObserver' in window) {

      const observer =
        new ResizeObserver(() => {

          validateCaptionWidth();

        });


      observer.observe(preview);

    } else {

      window.addEventListener(
        'resize',
        validateCaptionWidth
      );

    }


    /* Initial state */

    updatePersonalisationVisibility();

    updateCaptionPreview();

    updateCaptionColour();
  }


  /* ========================================
     FRAME SWITCHING
     ======================================== */

  function updateLayeredPreviewFrame(
    frameValue,
    sectionId
  ) {

    const previews =
      sectionId

        ? document.querySelectorAll(
            `[data-tp-preview][data-section-id="${sectionId}"]`
          )

        : document.querySelectorAll(
            '[data-tp-preview]'
          );


    previews.forEach((preview) => {

      const frame =
        preview.querySelector(
          '[data-tp-frame]'
        );


      if (!frame) return;


      const selectedFrame =
        normalise(frameValue);


      if (
        selectedFrame.includes('gold')
      ) {

        frame.src =
          preview.dataset.frameGold;

      } else if (
        selectedFrame.includes('silver')
      ) {

        frame.src =
          preview.dataset.frameSilver;

      } else if (
        selectedFrame.includes('black')
      ) {

        frame.src =
          preview.dataset.frameBlack;

      }

    });
  }


  /* Listen for frame variant changes */

  document.addEventListener(
    'change',
    (event) => {

      const target =
        event.target;


      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLSelectElement)
      ) {
        return;
      }


      const fieldset =
        target.closest('fieldset');


      const legendText =
        normalise(
          fieldset
            ?.querySelector('legend')
            ?.textContent
        );


      const inputName =
        normalise(
          target.name
        );


      const isFrameControl =
        inputName.includes('frame') ||
        legendText.includes('frame');


      if (!isFrameControl) return;


      const variantPicker =
        target.closest(
          '[data-section]'
        );


      const sectionId =
        variantPicker?.dataset.section || '';


      updateLayeredPreviewFrame(
        target.value,
        sectionId
      );

    }
  );


  /* ========================================
     INITIALISE
     ======================================== */

  function initialiseAll() {

    document
      .querySelectorAll(
        '[data-tp-controls]'
      )
      .forEach(
        initialiseControls
      );

  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialiseAll
    );

  } else {

    initialiseAll();

  }


  document.addEventListener(
    'shopify:section:load',
    initialiseAll
  );


})();